import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import {
  XMarkIcon,
  CameraIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useStaticPages } from "../useHook";
import { ImageWithFallback } from "../../../../Utils/loader";
import SelectField from "../../components/SelectField";

const emptyForm = {
  static_page_url: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  canonical_url: "",
  robots: "index,follow",
  og_type: "website",
  og_title: "",
  og_description: "",
  og_image: "",
  og_alt: "",
};

const ROBOTS_OPTIONS = [
  {
    value: "index,follow",
    label: "Index, Follow",
    hint: "Show page and follow links",
  },
  {
    value: "noindex,follow",
    label: "No Index, Follow",
    hint: "Hide page, still follow links",
  },
  {
    value: "index,nofollow",
    label: "Index, No Follow",
    hint: "Show page, do not follow links",
  },
  {
    value: "noindex,nofollow",
    label: "No Index, No Follow",
    hint: "Hide page and links",
  },
];

const OG_TYPE_OPTIONS = [
  { value: "website", label: "Website" },
  { value: "product", label: "Product" },
  { value: "article", label: "Article" },
];
const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

const REQUIRED_TEXT_FIELDS = [
  { key: "meta_title", label: "Meta Title" },
  { key: "meta_description", label: "Meta Description" },
  { key: "meta_keywords", label: "Meta Keywords" },
  { key: "canonical_url", label: "Canonical URL" },
  { key: "robots", label: "Robots" },
  { key: "og_type", label: "OG Type" },
];

const inputClass =
  "w-full h-[48px] px-4 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 transition-all duration-200";
const inputErrorClass =
  "w-full h-[48px] px-4 rounded-xl border border-red-400 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-red-100 transition-all duration-200";
const textareaClass =
  "w-full min-h-[96px] px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none resize-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 transition-all duration-200";
const textareaErrorClass =
  "w-full min-h-[96px] px-4 py-3 rounded-xl border border-red-400 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none resize-none focus:ring-2 focus:ring-red-100 transition-all duration-200";
const REQUIRED_MSG = "This field is required";
const labelClass = "block text-[13px] font-semibold text-slate-700 mb-1.5";
const sectionClass =
  "rounded-2xl border border-[#F0E4F0] bg-gradient-to-b from-[#FDFBFD] to-white p-4 sm:p-5 space-y-4";
const sectionTitleClass =
  "text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-primary";

function StaticPageModal({ isOpen, onClose, editData = null, onSuccess }) {
  const { getAvailableUrls, createStaticPage, updateStaticPage } =
    useStaticPages();
  const editingId = editData?.id || null;

  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [availableUrls, setAvailableUrls] = useState([]);
  const [urlsLoading, setUrlsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ogImageFile, setOgImageFile] = useState(null);
  const [ogImagePreview, setOgImagePreview] = useState(null);
  const [ogImageError, setOgImageError] = useState("");

  const fieldClass = (key, multiline = false) => {
    if (multiline) {
      return fieldErrors[key] ? textareaErrorClass : textareaClass;
    }
    return fieldErrors[key] ? inputErrorClass : inputClass;
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const resetOgImageState = () => {
    setOgImageFile(null);
    setOgImagePreview(null);
    setOgImageError("");
  };

  const resetFormState = () => {
    setForm(emptyForm);
    setFieldErrors({});
    setAvailableUrls([]);
    resetOgImageState();
  };

  useEffect(() => {
    if (!isOpen) return;

    if (editData) {
      setForm({
        static_page_url: editData.static_page_url || "",
        meta_title: editData.meta_title || "",
        meta_description: editData.meta_description || "",
        meta_keywords: editData.meta_keywords || "",
        canonical_url: editData.canonical_url || "",
        robots: editData.robots || "index,follow",
        og_type: editData.og_type || "website",
        og_title: editData.og_title || "",
        og_description: editData.og_description || "",
        og_image: editData.og_image || "",
        og_alt: editData.og_alt || "",
      });
      setOgImageFile(null);
      setOgImagePreview(editData.og_image || null);
      setOgImageError("");
      setFieldErrors({});
      setAvailableUrls([]);
    } else {
      resetFormState();
    }
  }, [isOpen, editData]);

  useEffect(() => {
    if (!isOpen || editingId) return undefined;
    let cancelled = false;
    (async () => {
      setUrlsLoading(true);
      try {
        const urls = await getAvailableUrls();
        if (!cancelled) setAvailableUrls(urls);
      } catch {
        if (!cancelled) setAvailableUrls([]);
      } finally {
        if (!cancelled) setUrlsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, editingId, getAvailableUrls]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const handleOgImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setOgImageError("Please upload a valid image file.");
      setOgImageFile(null);
      setOgImagePreview(null);
      return;
    }

    if (ogImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(ogImagePreview);
    }

    const preview = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== OG_IMAGE_WIDTH || img.height !== OG_IMAGE_HEIGHT) {
        URL.revokeObjectURL(preview);
        setOgImageError(
          `Image must be exactly ${OG_IMAGE_WIDTH}x${OG_IMAGE_HEIGHT} pixels.`,
        );
        setOgImageFile(null);
        setOgImagePreview(null);
        return;
      }
      setOgImageError("");
      setOgImageFile(file);
      setOgImagePreview(preview);
      setForm((prev) => ({ ...prev, og_image: "" }));
    };
    img.onerror = () => {
      URL.revokeObjectURL(preview);
      setOgImageError("Invalid image file.");
      setOgImageFile(null);
      setOgImagePreview(null);
    };
    img.src = preview;
  };

  const handleRemoveOgImage = () => {
    if (ogImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(ogImagePreview);
    }
    setOgImageFile(null);
    setOgImagePreview(null);
    setOgImageError(REQUIRED_MSG);
    setForm((prev) => ({ ...prev, og_image: "" }));
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "og_image") return;
      if (editingId && key === "static_page_url") return;
      if (value != null && value !== "") fd.append(key, String(value));
    });
    if (ogImageFile) {
      fd.append("og_image", ogImageFile);
    }
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};

    if (!editingId && !String(form.static_page_url || "").trim()) {
      errors.static_page_url = REQUIRED_MSG;
    }

    REQUIRED_TEXT_FIELDS.forEach(({ key }) => {
      if (!String(form[key] || "").trim()) errors[key] = REQUIRED_MSG;
    });

    const hasImage = Boolean(ogImageFile || (editingId && ogImagePreview));
    if (!hasImage) {
      setOgImageError(REQUIRED_MSG);
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0 || !hasImage) {
      return;
    }

    if (
      ogImageError &&
      ogImageError !== REQUIRED_MSG &&
      ogImageError !== "OG Image is required."
    ) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildFormData();
      if (editingId) {
        await updateStaticPage(editingId, payload);
        Swal.fire(
          "Updated",
          "Static page SEO updated successfully.",
          "success",
        );
      } else {
        await createStaticPage(payload);
        Swal.fire(
          "Created",
          "Static page SEO created successfully.",
          "success",
        );
      }
      handleClose();
      if (onSuccess) await onSuccess();
    } catch (error) {
      Swal.fire("Error", error.message || "Something went wrong", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const pageUrlOptions = availableUrls.map((url) => ({
    value: url,
    label: url,
  }));

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#15173D]/55 backdrop-blur-[3px] p-4">
      <div className="bg-white w-[680px] max-w-full max-h-[90vh] rounded-[28px] overflow-hidden relative flex flex-col shadow-[0_28px_80px_rgba(21,23,61,0.28)]">
        <div className="brand-gradient px-6 py-5 flex items-start justify-between shrink-0">
          <div>
            <p className="text-white/70 text-[11px] uppercase tracking-[0.22em] font-medium">
              SEO Management
            </p>
            <h3 className="text-white text-[22px] font-semibold mt-1">
              {editingId ? "Edit Static Page SEO" : "Add Static Page SEO"}
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-white/80 hover:text-white hover:bg-white/15 rounded-full p-1.5 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col min-h-0 flex-1"
          noValidate
        >
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 seo-modal-panel">
            {!editingId ? (
              <section className={sectionClass}>
                <h4 className={sectionTitleClass}>Target page</h4>
                <div>
                  <label className={labelClass}>
                    Page URL <span className="text-brand-error">*</span>
                  </label>
                  <SelectField
                    value={form.static_page_url}
                    onChange={(value) => setField("static_page_url", value)}
                    placeholder={
                      urlsLoading ? "Loading URLs..." : "Select page URL"
                    }
                    options={pageUrlOptions}
                    error={Boolean(fieldErrors.static_page_url)}
                    disabled={urlsLoading}
                  />
                  {fieldErrors.static_page_url && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.static_page_url}
                    </p>
                  )}
                  {!urlsLoading && availableUrls.length === 0 && (
                    <p className="text-amber-600 text-xs mt-1.5">
                      All configured static pages already have SEO entries.
                    </p>
                  )}
                </div>
              </section>
            ) : (
              <div className="rounded-2xl bg-[#F8EEF8] border border-[#EBD6EB] px-4 py-3 flex flex-wrap items-center gap-2 text-sm">
                <span className="inline-flex items-center rounded-full bg-brand-primary text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">
                  Page URL
                </span>
                <span className="font-mono text-xs font-semibold text-brand-primary">
                  {form.static_page_url}
                </span>
              </div>
            )}

            <section className={sectionClass}>
              <h4 className={sectionTitleClass}>Search engine</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Meta Title <span className="text-brand-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.meta_title}
                    onChange={(e) => setField("meta_title", e.target.value)}
                    className={fieldClass("meta_title")}
                    placeholder="Page title for search engines"
                  />
                  {fieldErrors.meta_title && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.meta_title}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Meta Description <span className="text-brand-error">*</span>
                  </label>
                  <textarea
                    value={form.meta_description}
                    onChange={(e) =>
                      setField("meta_description", e.target.value)
                    }
                    rows={3}
                    className={fieldClass("meta_description", true)}
                    placeholder="Short description for search results"
                  />
                  {fieldErrors.meta_description && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.meta_description}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Meta Keywords <span className="text-brand-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.meta_keywords}
                    onChange={(e) => setField("meta_keywords", e.target.value)}
                    className={fieldClass("meta_keywords")}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                  {fieldErrors.meta_keywords && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.meta_keywords}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Canonical URL <span className="text-brand-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.canonical_url}
                    onChange={(e) => setField("canonical_url", e.target.value)}
                    className={fieldClass("canonical_url")}
                    placeholder="https://example.com/page"
                  />
                  {fieldErrors.canonical_url && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.canonical_url}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Robots <span className="text-brand-error">*</span>
                  </label>
                  <SelectField
                    value={form.robots}
                    onChange={(value) => setField("robots", value)}
                    placeholder="Select robots"
                    options={ROBOTS_OPTIONS}
                    error={Boolean(fieldErrors.robots)}
                  />
                  {fieldErrors.robots && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.robots}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h4 className={sectionTitleClass}>Social share</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    OG Type <span className="text-brand-error">*</span>
                  </label>
                  <SelectField
                    value={form.og_type}
                    onChange={(value) => setField("og_type", value)}
                    placeholder="Select OG type"
                    options={OG_TYPE_OPTIONS}
                    error={Boolean(fieldErrors.og_type)}
                  />
                  {fieldErrors.og_type && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_type}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>OG Title</label>
                  <input
                    type="text"
                    value={form.og_title}
                    onChange={(e) => setField("og_title", e.target.value)}
                    className={fieldClass("og_title")}
                    placeholder="Title for social previews"
                  />
                  {fieldErrors.og_title && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_title}
                    </p>
                  )}
                  {!fieldErrors.og_title && (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Optional. Uses meta title if left empty.
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    OG Image <span className="text-brand-error">*</span>
                  </label>

                  {ogImagePreview ? (
                    <div className="relative">
                      <div
                        className={`w-full h-44 bg-slate-100 rounded-2xl overflow-hidden border ${
                          ogImageError ? "border-red-400" : "border-[#EBD6EB]"
                        }`}
                      >
                        <ImageWithFallback
                          src={ogImagePreview}
                          alt="OG preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveOgImage}
                        className="absolute top-3 right-3 bg-brand-error hover:bg-red-600 text-white p-2 rounded-full transition-colors shadow-lg"
                        title="Remove image"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="static-og-image-upload"
                      className={`cursor-pointer w-full h-44 bg-[#FBF6FB] border-2 border-dashed rounded-2xl flex flex-col items-center justify-center hover:bg-[#F8EEF8] hover:border-brand-secondary transition-colors ${
                        ogImageError ? "border-red-400" : "border-[#E0C8E0]"
                      }`}
                    >
                      <span className="h-12 w-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-2">
                        <CameraIcon className="h-6 w-6 text-brand-primary" />
                      </span>
                      <span className="text-slate-700 font-semibold text-sm">
                        Upload OG Image
                      </span>
                      <span className="text-brand-error text-xs mt-1 font-semibold">
                        {OG_IMAGE_WIDTH}x{OG_IMAGE_HEIGHT} required
                      </span>
                      <span className="text-slate-400 text-xs mt-1">
                        Prefer JPG/JPEG images
                      </span>
                    </label>
                  )}

                  <input
                    id="static-og-image-upload"
                    type="file"
                    accept="image/jpeg,.jpg,.jpeg,image/*"
                    onChange={handleOgImageChange}
                    className="hidden"
                  />

                  {ogImageError ? (
                    <p className="text-brand-error text-xs mt-1.5">
                      {ogImageError}
                    </p>
                  ) : (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Required size: {OG_IMAGE_WIDTH}x{OG_IMAGE_HEIGHT}px.
                      Prefer JPG/JPEG.
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>OG Alt</label>
                  <input
                    type="text"
                    value={form.og_alt}
                    onChange={(e) => setField("og_alt", e.target.value)}
                    className={fieldClass("og_alt")}
                    placeholder="Alt text for OG image"
                  />
                  {fieldErrors.og_alt && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_alt}
                    </p>
                  )}
                  {!fieldErrors.og_alt && (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Optional. Uses OG title, then meta title, if left empty.
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>OG Description</label>
                  <textarea
                    value={form.og_description}
                    onChange={(e) => setField("og_description", e.target.value)}
                    rows={2}
                    className={fieldClass("og_description", true)}
                    placeholder="Description for social previews"
                  />
                  {fieldErrors.og_description && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_description}
                    </p>
                  )}
                  {!fieldErrors.og_description && (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Optional. Uses meta description if left empty.
                    </p>
                  )}
                </div>
              </div>
            </section>
          </div>

          <div className="px-6 py-4 border-t border-[#F0E4F0] bg-white flex justify-end items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-reset-filter justify-center min-w-[108px]"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`btn justify-center min-w-[108px] ${
                editingId ? "btn-edit text-white" : "btn-primary"
              } ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {submitting ? "Saving..." : editingId ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

export default StaticPageModal;
